import React, { useMemo, useState, useEffect } from 'react';
import { Container, Row, Col, Card, Button, Table, Form, Modal, Alert } from 'react-bootstrap';
import { Link, useLocation, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { useNotification, NotificationMessages } from '../hooks/useNotification';

const LineItemBudgetForm = () => {
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const projectId = location.state?.projectId || searchParams.get('projectId') || null;
  let proposal = location.state?.proposal || null;
  if (!proposal && !projectId) {
    try {
      const saved = window.sessionStorage.getItem('extensync_proposal_for_budget');
      if (saved) proposal = JSON.parse(saved);
    } catch {}
  }

  // State for project data
  const [projectData, setProjectData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [coordinatorDepartment, setCoordinatorDepartment] = useState(null);
  const [existingBudget, setExistingBudget] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  
  // Notification system
  const { notification, showSuccess, showError, dismiss } = useNotification();

  // Clear any existing notifications on component mount
  useEffect(() => {
    dismiss();
  }, [dismiss]);

  // Fetch project data when projectId exists
  useEffect(() => {
    if (projectId) {
      console.log('🔍 DEBUG - Fetching project data for projectId:', projectId);
      const fetchProjectData = async () => {
        try {
          setLoading(true);
          setError(null); // Clear any previous errors
          const response = await axios.get(`/projects/${projectId}?t=${Date.now()}`);
          setProjectData(response.data);
          console.log('🔍 DEBUG - Fetched project data:', response.data);
          // Clear any existing notifications when project data is successfully loaded
          dismiss();
          console.log('🔍 DEBUG - Assistant names:', response.data.assistant_project_leader_names);
          console.log('🔍 DEBUG - Assistant names type:', typeof response.data.assistant_project_leader_names);
          console.log('🔍 DEBUG - Assistant names value:', JSON.stringify(response.data.assistant_project_leader_names));
          console.log('🔍 DEBUG - Coordinator names:', response.data.coordinator_names);
          console.log('🔍 DEBUG - Coordinator names type:', typeof response.data.coordinator_names);
          console.log('🔍 DEBUG - Coordinator names value:', JSON.stringify(response.data.coordinator_names));
          console.log('🔍 DEBUG - Project Leader name:', response.data.project_leader_name);
          console.log('🔍 DEBUG - Project Leader task:', response.data.project_leader_task);
          console.log('🔍 DEBUG - Start date:', response.data.start_date);
          console.log('🔍 DEBUG - End date:', response.data.end_date);
          console.log('🔍 DEBUG - Start date type:', typeof response.data.start_date);
          console.log('🔍 DEBUG - End date type:', typeof response.data.end_date);
          console.log('🔍 DEBUG - Raw start date from server:', JSON.stringify(response.data.start_date));
          console.log('🔍 DEBUG - Raw end date from server:', JSON.stringify(response.data.end_date));
          
          // Get coordinator department from coordinator_id (singular)
          console.log('🔍 DEBUG - Project data:', response.data);
          console.log('🔍 DEBUG - Coordinator ID:', response.data.coordinator_id);
          console.log('🔍 DEBUG - Coordinator ID type:', typeof response.data.coordinator_id);
          
          if (response.data.coordinator_id) {
            try {
              const allUsersResponse = await axios.get('/users');
              console.log('🔍 DEBUG - All users response:', allUsersResponse.data);
              
              const coordinator = allUsersResponse.data.find(user => 
                user.user_id === response.data.coordinator_id
              );
              
              if (coordinator) {
                console.log('🔍 DEBUG - Found coordinator:', coordinator);
                if (coordinator.department_college) {
                  setCoordinatorDepartment(coordinator.department_college);
                  console.log('🔍 DEBUG - Set coordinator department to:', coordinator.department_college);
                } else {
                  console.log('🔍 DEBUG - Coordinator has no department_college');
                }
              } else {
                console.log('🔍 DEBUG - Coordinator not found in users list');
              }
            } catch (allUsersError) {
              console.error('Error fetching all users:', allUsersError);
            }
          } else {
            console.log('🔍 DEBUG - No coordinator ID found');
          }
        } catch (error) {
          console.error('Error fetching project data:', error);
          setError('Failed to load project data. Please check if the project exists and try again.');
          setProjectData(null);
        } finally {
          setLoading(false);
        }
      };
      fetchProjectData();
      
      // Fetch existing budget data
      const fetchBudgetData = async () => {
        try {
          console.log('🔍 DEBUG - Fetching existing budget data for projectId:', projectId);
          const response = await axios.get(`/budgets/by-project/${projectId}`);
          if (response.data) {
            console.log('🔍 DEBUG - Found existing budget:', response.data);
            setExistingBudget(response.data);
            setIsEditing(true);
            
            // Load existing amounts into the form
            try {
              const existingItems = JSON.parse(response.data.items_json || '{}');
              const existingTotals = JSON.parse(response.data.totals_json || '{}');
              
              console.log('🔍 DEBUG - Existing items:', existingItems);
              console.log('🔍 DEBUG - Existing totals:', existingTotals);
              
              // Update the amounts state with existing data
              setAmounts(prev => {
                const updated = { ...prev };
                Object.keys(existingItems).forEach(key => {
                  if (existingItems[key] !== undefined && existingItems[key] !== null) {
                    updated[key] = existingItems[key].toString();
                  }
                });
                return updated;
              });
            } catch (parseError) {
              console.error('Error parsing existing budget data:', parseError);
            }
          } else {
            console.log('🔍 DEBUG - No existing budget found, creating new one');
            setIsEditing(false);
          }
        } catch (error) {
          console.log('🔍 DEBUG - No existing budget found or error fetching:', error.message);
          setIsEditing(false);
        }
      };
      fetchBudgetData();
    }
  }, [projectId]);

  const formatDuration = (duration) => {
    if (!duration) return '';
    const { startDate, endDate, startTime, endTime } = duration;
    const datePart = startDate && endDate ? `${startDate} to ${endDate}` : startDate || '';
    const timePart = startTime && endTime ? `${startTime} - ${endTime}` : startTime || '';
    return [datePart, timePart].filter(Boolean).join(' • ');
  };

  // Format ISO date string to YYYY-MM-DD format (using local timezone)
  const formatDate = (dateString) => {
    if (!dateString) return '';
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return dateString; // Return original if invalid
      
      // Use local timezone to avoid timezone conversion issues
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      
      return `${year}-${month}-${day}`;
    } catch (error) {
      console.error('Error formatting date:', error);
      return dateString; // Return original if error
    }
  };

  // Helper function to extract names from various data formats
  const extractNames = (names) => {
    console.log('🔍 DEBUG - extractNames input:', names, 'type:', typeof names);
    
    if (!names) return '-';
    
    // Helper to remove task from name (format: "Name - Task")
    const removeTask = (name) => {
      if (typeof name === 'string' && name.includes(' - ')) {
        return name.split(' - ')[0].trim();
      }
      return name;
    };
    
    // Handle string that looks like an array (e.g., '["Head"]' or '["Head", "Manager"]')
    if (typeof names === 'string') {
      // Check if it's a stringified array
      if (names.startsWith('[') && names.endsWith(']')) {
        try {
          const parsed = JSON.parse(names);
          console.log('🔍 DEBUG - Parsed array:', parsed);
          if (Array.isArray(parsed)) {
            const result = parsed.map(removeTask).join(', ');
            console.log('🔍 DEBUG - Joined result:', result);
            return result;
          }
        } catch (e) {
          console.log('🔍 DEBUG - JSON.parse failed:', e);
          // If JSON.parse fails, return the string as is (with task removed)
          return removeTask(names);
        }
      }
      return removeTask(names);
    }
    
    if (Array.isArray(names)) {
      const result = names.map(removeTask).join(', ');
      console.log('🔍 DEBUG - Array joined result:', result);
      return result;
    }
    
    if (typeof names === 'object') {
      // Handle object format like {"Head": "value"} or {"Head": true}
      const keys = Object.keys(names);
      if (keys.length > 0) {
        const result = keys.map(removeTask).join(', ');
        console.log('🔍 DEBUG - Object keys result:', result);
        return result;
      }
    }
    
    return '-';
  };

  // Budget items schema
  const mooeItems = useMemo(() => ([
    { key: 'travel_local', label: 'Travelling Cost - Local' },
    { key: 'travel_foreign', label: 'Travelling Cost - Foreign' },
    { key: 'training', label: 'Training Expenses' },
    { key: 'office_supplies', label: 'Supplies and Materials - Office Supplies' },
    { key: 'accountable_forms', label: 'Supplies and Materials - Accountable Forms' },
    { key: 'drugs_medicines', label: 'Supplies and Materials - Drugs and Medicines' },
    { key: 'laboratory_exp', label: 'Supplies and Materials - Laboratory Expenses' },
    { key: 'textbooks', label: 'Supplies and Materials - Textbook and Instructional Materials' },
    { key: 'supplies_others', label: 'Supplies and Materials - Others' },
    { key: 'postage', label: 'Postage and Deliveries' },
    { key: 'tel_mobile', label: 'Communication Expenses - Telephone (Mobile)' },
    { key: 'internet', label: 'Communication Expenses - Internet' },
    { key: 'comm_others', label: 'Communication Expenses - Others' },
    { key: 'rent', label: 'Rent Expenses' },
    { key: 'transport_delivery', label: 'Transportation and Delivery Expenses' },
    { key: 'subscription', label: 'Subscription Expenses' },
    { key: 'consultancy', label: 'Professional Services - Consultancy Services' },
    { key: 'general_services', label: 'Professional Services - General Services' },
    { key: 'other_professional', label: 'Professional Services - Other Professional Services' },
    { key: 'repair_it', label: 'Repairs and Maintenance of Facilities - IT Equipment and Software' },
    { key: 'repair_lab', label: 'Repairs and Maintenance of Facilities - Laboratory Equipment' },
    { key: 'repair_tech', label: 'Repairs and Maintenance - Technical and Scientific Equipment' },
    { key: 'repair_machine', label: 'Repairs and Maintenance - Machineries and Equipment' },
    { key: 'repair_others', label: 'Repairs and Maintenance - Others' },
    { key: 'taxes', label: 'Taxes, Duties, Patent, and Licenses' },
    { key: 'other_mooe', label: 'Other Maintenance and Operating Expenses' },
    { key: 'representation', label: 'Representation Expenses' },
    { key: 'advertising', label: 'Advertising Expenses' },
    { key: 'printing_binding', label: 'Printing and Binding Expenses' },
    { key: 'others_financial_charges', label: 'Others Financial Charges (Contingency = 10% of the Total Project Cost)' },
  ]), []);

  const equipmentItems = useMemo(() => ([
    { key: 'equip_it', label: 'IT Equipment and Software' },
    { key: 'equip_machineries', label: 'Machineries' },
    { key: 'equip_comm', label: 'Communication Equipment' },
    { key: 'equip_lab', label: 'Laboratory Equipment' },
    { key: 'equip_tech_sci', label: 'Technical and Scientific Equipment' },
    { key: 'equip_others', label: 'Others' },
  ]), []);

  const initialAmounts = useMemo(() => {
    const base = {};
    [...mooeItems, ...equipmentItems].forEach(i => { base[i.key] = ''; });
    return base;
  }, [mooeItems, equipmentItems]);

  const [amounts, setAmounts] = useState(() => {
    const base = {};
    // Initialize with empty strings for all budget items
    const allItems = [
      { key: 'travel_local' }, { key: 'travel_foreign' }, { key: 'training' },
      { key: 'office_supplies' }, { key: 'accountable_forms' }, { key: 'drugs_medicines' },
      { key: 'laboratory_exp' }, { key: 'textbooks' }, { key: 'supplies_others' },
      { key: 'postage' }, { key: 'tel_mobile' }, { key: 'internet' }, { key: 'comm_others' },
      { key: 'rent' }, { key: 'transport_delivery' }, { key: 'subscription' },
      { key: 'consultancy' }, { key: 'general_services' }, { key: 'other_professional' },
      { key: 'repair_it' }, { key: 'repair_lab' }, { key: 'repair_tech' },
      { key: 'repair_machine' }, { key: 'repair_others' }, { key: 'taxes' },
      { key: 'other_mooe' }, { key: 'representation' }, { key: 'advertising' },
      { key: 'printing_binding' }, { key: 'others_financial_charges' },
      { key: 'equip_it' }, { key: 'equip_machineries' }, { key: 'equip_comm' },
      { key: 'equip_lab' }, { key: 'equip_tech_sci' }, { key: 'equip_others' }
    ];
    allItems.forEach(item => { base[item.key] = ''; });
    return base;
  });

  const parseNumber = (v) => {
    const num = parseFloat(v);
    return isNaN(num) ? 0 : num;
  };

  const mooeSubtotal = useMemo(() => mooeItems.reduce((sum, i) => sum + parseNumber(amounts[i.key]), 0), [amounts, mooeItems]);
  const equipmentSubtotal = useMemo(() => equipmentItems.reduce((sum, i) => sum + parseNumber(amounts[i.key]), 0), [amounts, equipmentItems]);
  const baseTotal = useMemo(() => mooeSubtotal + equipmentSubtotal, [mooeSubtotal, equipmentSubtotal]);
  const contingency = 0; // Excluded per request
  const grandTotal = useMemo(() => +baseTotal.toFixed(2), [baseTotal]);

  const handleAmountChange = (key, value) => {
    // allow empty string for ease of editing
    if (value === '') {
      setAmounts(prev => ({ ...prev, [key]: '' }));
      return;
    }
    const cleaned = value.replace(/[^0-9.]/g, '');
    setAmounts(prev => ({ ...prev, [key]: cleaned }));
  };

  const currency = (n) => n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  // Handle initial submit - show confirmation modal
  const handleSubmitClick = (e) => {
    e.preventDefault();
    setShowConfirmModal(true);
  };

  // Handle actual budget submission after modal confirmation
  const handleSubmitBudget = async () => {
    if (submitting) return;
    
    try {
      setSubmitting(true);
      setShowConfirmModal(false);
      // Debug: Log the data received
      console.log('🔍 DEBUG - LineItemBudgetForm data:');
      console.log('projectId:', projectId);
      console.log('projectData:', projectData);
      console.log('proposal:', proposal);

      // Validate required fields
      if (!projectId && !proposal?.title) {
        showError('Project title is required. Please ensure you have a valid project selected.');
        return;
      }

      // 1) Use existing project ID or create new project
      let finalProjectId;
      if (projectId) {
        // Project already exists (coming from ProjectProposal with STF/MDS)
        finalProjectId = projectId;
        console.log('🔍 DEBUG - Using existing project ID:', finalProjectId);
      } else {
        // Create new project from proposal data (legacy flow)
        const fullData = proposal?.fullFormData || {};
        const payload = {
          title: proposal?.title || '',
          start_date: proposal?.duration?.startDate || '',
          end_date: proposal?.duration?.endDate || '',
          start_time: proposal?.duration?.startTime || '',
          end_time: proposal?.duration?.endTime || '',
          project_leader_name: proposal?.project_leader_name || '',
          project_leader_task: proposal?.project_leader_task || '',
          assistant_project_leader_names: Array.isArray(proposal?.assistant_project_leader_names) 
            ? proposal.assistant_project_leader_names.join(', ')
            : (proposal?.assistant_project_leader_names || ''),
          assistant_project_leader_tasks: proposal?.assistant_project_leader_tasks || {},
          coordinator_names: Array.isArray(proposal?.coordinator_names) 
            ? proposal.coordinator_names.join(', ')
            : (proposal?.coordinator_names || ''),
          coordinator_tasks: proposal?.coordinator_tasks || {},
          request_type: fullData.request_type || '',
          event_type: fullData.event_type || '',
          location: fullData.location || '',
          extension_agenda: fullData.extension_agenda || [],
          sdg_goals: fullData.sdg_goals || [],
          offices_involved: fullData.offices_involved || '',
          programs_involved: fullData.programs_involved || '',
          partner_agencies: fullData.partner_agencies || '',
          beneficiaries: fullData.beneficiaries || '',
          total_cost: fullData.total_cost || '',
          fund_source: fullData.fund_source || [],
          rationale: fullData.rationale || '',
          objectives: fullData.objectives || '',
          expected_output: fullData.expected_output || '',
          strategies_methods: fullData.strategies_methods || '',
          financial_plan_details: fullData.financial_plan_details || '',
          functional_relationships: fullData.functional_relationships || '',
          sustainability_plan: fullData.sustainability_plan || '',
          monitoring_evaluation_table: fullData.monitoring_evaluation_table || {}
        };

        console.log('🔍 DEBUG - Creating new project with payload:', payload);
        const { data: createdProject } = await axios.post('/projects', payload);
        finalProjectId = createdProject.project_id;
        console.log('🔍 DEBUG - Created new project with ID:', finalProjectId);
      }

      // 2) Save the budget linked to the project
      const budgetPayload = {
        proposal: { 
          ...proposal, 
          project_id: finalProjectId,
          title: projectData?.title || proposal?.title || 'Untitled Project'
        },
        items: amounts,
        totals: {
          mooeSubtotal,
          equipmentSubtotal,
          contingency,
          grandTotal
        }
      };

      // Debug: Log the budget payload being sent
      console.log('🔍 DEBUG - Budget payload:', budgetPayload);
      console.log('🔍 DEBUG - Proposal title:', budgetPayload.proposal.title);
      console.log('🔍 DEBUG - Items:', budgetPayload.items);
      console.log('🔍 DEBUG - Totals:', budgetPayload.totals);
      
      if (isEditing && existingBudget) {
        // Update existing budget
        console.log('🔍 DEBUG - Updating existing budget:', existingBudget.budget_id);
        await axios.put(`/budgets/${existingBudget.budget_id}`, budgetPayload);
        showSuccess(NotificationMessages.BUDGET_UPDATED);
      } else {
        // Create new budget
        console.log('🔍 DEBUG - Creating new budget');
        await axios.post('/budgets', budgetPayload);
        showSuccess(NotificationMessages.BUDGET_CREATED);
      }
      // 3) Go to the project view after a short delay to show success message
      window.sessionStorage.removeItem('extensync_proposal_for_budget');
      setTimeout(() => {
        window.location.href = `/projects/${finalProjectId}`;
      }, 1500);
    } catch (err) {
      // Surface backend details to aid debugging
      console.error('Budget submit failed:', err.response?.data || err);
      
      if (err.response?.status === 400) {
        const errors = err.response?.data?.errors;
        if (errors && errors.length > 0) {
          const errorMessages = errors.map(error => `${error.param}: ${error.msg}`).join(', ');
          showError(`Validation errors: ${errorMessages}`);
        } else {
          showError('Invalid request. Please check your input and try again.');
        }
      } else if (err.response?.status === 404) {
        showError('Project not found. Please refresh the page and try again.');
      } else if (err.response?.status === 500) {
        showError('Server error occurred. Please try again later.');
      } else if (err.code === 'NETWORK_ERROR' || !err.response) {
        showError('Network error. Please check your connection and try again.');
      } else {
        const msg = err.response?.data?.details || err.response?.data?.error || err.message || 'Failed to submit Line-Item Budget. Please try again.';
        showError(msg);
      }
    } finally {
      setSubmitting(false);
    }
  };

  // Show error state if there's an error and no project data
  if (error && !projectData) {
    return (
      <div className="bg-light min-vh-100 pt-4">
        <div className="container-fluid px-4">
          <Card className="shadow border-0">
            <Card.Body className="p-4 text-center">
              <i className="bi bi-exclamation-triangle-fill text-danger" style={{ fontSize: '3rem' }}></i>
              <h4 className="mt-3 mb-3">Project Not Found</h4>
              <p className="text-muted mb-4">{error}</p>
              <div className="d-flex justify-content-center gap-2">
                <Button as={Link} to="/projects" variant="secondary">
                  Back to Projects
                </Button>
                <Button variant="primary" onClick={() => window.location.reload()}>
                  Refresh Page
                </Button>
              </div>
            </Card.Body>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-light min-vh-100 pt-4">
      <div className="container-fluid px-4">
        {/* Notification Display */}
        {notification.show && (
          <Alert variant={notification.variant} dismissible onClose={dismiss} className="mt-3">
            {notification.message}
          </Alert>
        )}
        <Card className="shadow border-0">
          <Card.Body className="p-4">
                <h4 className="mb-3">Extension Line-Item Budget</h4>
                
                {/* Accountability Information */}
                {existingBudget && (
                  <div className="alert alert-info mb-3">
                    <div className="row">
                      <div className="col-md-6">
                        <small className="text-muted">
                          <strong>Project Coordinator:</strong> {existingBudget.coordinator_name || 'Unknown Coordinator'} 
                          {existingBudget.coordinator_role && ` (${existingBudget.coordinator_role})`}
                        </small>
                        <br />
                        <small className="text-muted">
                          <strong>Budget Created:</strong> {new Date(existingBudget.created_at).toLocaleString()}
                        </small>
                      </div>
                      <div className="col-md-6">
                        {existingBudget.updated_at && existingBudget.updated_at !== existingBudget.created_at && (
                          <>
                            <small className="text-muted">
                              <strong>Last updated:</strong> {new Date(existingBudget.updated_at).toLocaleString()}
                            </small>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                <div className="mb-4">
                  {loading ? (
                    <div className="text-center py-3">
                      <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">Loading...</span>
                      </div>
                      <div className="mt-2">Loading project data...</div>
                    </div>
                  ) : !projectData && !proposal ? (
                    <div className="text-center py-3">
                      <i className="bi bi-exclamation-triangle text-warning" style={{ fontSize: '2rem' }}></i>
                      <div className="mt-2 text-muted">No project data available</div>
                    </div>
                  ) : (
                    <>
                      <div className="mb-2"><strong>Title:</strong> {projectData?.title || proposal?.title || '-'}</div>
                      <div className="mb-2"><strong>Project Leader:</strong> {(() => {
                        const leaderName = projectData?.project_leader_name || proposal?.project_leader_name || '';
                        // Remove task if present (format: "Name - Task" or "Honorific Name - Task")
                        return leaderName.includes(' - ') ? leaderName.split(' - ')[0].trim() : leaderName || '-';
                      })()}</div>
                      <div className="mb-2"><strong>Assistant Project Leaders:</strong> {(() => {
                        console.log('🔍 DEBUG - Display Assistant names:', projectData?.assistant_project_leader_names);
                        return projectData?.assistant_project_leader_names ? extractNames(projectData.assistant_project_leader_names) : '-';
                      })()}</div>
                      <div className="mb-2"><strong>Coordinators:</strong> {(() => {
                        console.log('🔍 DEBUG - Display Coordinator names:', projectData?.coordinator_names);
                        return projectData?.coordinator_names ? extractNames(projectData.coordinator_names) : '-';
                      })()}</div>
                      <div className="mb-2"><strong>Campus/College:</strong> {coordinatorDepartment || projectData?.campus_college || proposal?.campusCollege || '-'}</div>
                      <div className="mb-2"><strong>Duration:</strong> {(() => {
                        console.log('🔍 DEBUG - Duration display - projectData:', projectData);
                        console.log('🔍 DEBUG - Duration display - start_date:', projectData?.start_date);
                        console.log('🔍 DEBUG - Duration display - end_date:', projectData?.end_date);
                        console.log('🔍 DEBUG - Duration display - formatDate start:', formatDate(projectData?.start_date));
                        console.log('🔍 DEBUG - Duration display - formatDate end:', formatDate(projectData?.end_date));
                        
                        if (projectData) {
                          const startFormatted = formatDate(projectData.start_date);
                          const endFormatted = formatDate(projectData.end_date);
                          return `${startFormatted} to ${endFormatted}`;
                        } else {
                          return formatDuration(proposal?.duration);
                        }
                      })()}</div>
                    </>
                  )}
                </div>

                {/* Only show budget form if we have project data */}
                {projectData && (
                  <>
                    <div className="table-responsive mb-3">
                      <Table bordered hover>
                      <thead>
                        <tr className="table-secondary">
                          <th style={{ width: '70%' }}>ITEM</th>
                          <th style={{ width: '30%' }}>AMOUNT (Php)</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr className="table-light">
                          <td colSpan={2} className="fw-bold">I. Maintenance and Other Operating Expenses (MOOE)</td>
                        </tr>
                        {mooeItems.map(item => (
                          <tr key={item.key}>
                            <td>{item.label}</td>
                            <td>
                              <Form.Control
                                type="number"
                                min="0"
                                step="0.01"
                                value={amounts[item.key]}
                                onChange={(e) => handleAmountChange(item.key, e.target.value)}
                              />
                            </td>
                          </tr>
                        ))}

                        <tr className="table-light">
                          <td colSpan={2} className="fw-bold">II. Equipment Outlay</td>
                        </tr>
                        {equipmentItems.map(item => (
                          <tr key={item.key}>
                            <td>{item.label}</td>
                            <td>
                              <Form.Control
                                type="number"
                                min="0"
                                step="0.01"
                                value={amounts[item.key]}
                                onChange={(e) => handleAmountChange(item.key, e.target.value)}
                              />
                            </td>
                          </tr>
                        ))}

                        {/* Removed contingency row per request */}
                        <tr className="table-primary">
                          <td className="text-end fw-bold">TOTAL PROGRAM/PROJECT COST</td>
                          <td className="fw-bold">{currency(grandTotal)}</td>
                        </tr>
                      </tbody>
                      </Table>
                    </div>

                    <div className="d-flex justify-content-end gap-2">
                      <Button as={Link} to="/projects/new" variant="secondary">Back to Proposal</Button>
                      <Button 
                        id="submit-budget-btn" 
                        variant="primary" 
                        onClick={handleSubmitClick}
                        disabled={submitting}
                      >
                        {submitting ? 'Submitting...' : 'Submit'}
                      </Button>
                    </div>
                  </>
                )}
          </Card.Body>
        </Card>
      </div>
      
      {/* Submit Confirmation Modal */}
      <Modal show={showConfirmModal} onHide={() => setShowConfirmModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Confirm Line-Item Budget Submission</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="text-center py-3">
            <i className="bi bi-check-circle-fill text-success" style={{ fontSize: '3rem' }}></i>
            <h5 className="mt-3 mb-2">Confirm Line-Item Budget Submission</h5>
            <p className="text-muted">Are you sure that all budget details are correct?</p>
            {isEditing ? (
              <div className="alert alert-info">
                <strong>Note:</strong> This will update the existing Line-Item Budget for this project.
              </div>
            ) : (
              <div className="alert alert-info">
                <strong>Note:</strong> This will create a new Line-Item Budget for this project.
              </div>
            )}
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowConfirmModal(false)}>Cancel</Button>
          <Button variant="success" onClick={handleSubmitBudget} disabled={submitting}>
            {submitting ? 'Submitting...' : (isEditing ? 'Update Budget' : 'Submit Budget')}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default LineItemBudgetForm;


